<template>
	<div class="device-page-container">
		<div class="device-search-container">
			<input class="device-search-input" type="text" v-model="this.searchKeyword"
			       placeholder="输入账户邮箱 / 设备ID搜索" @keyup.enter="onSearchClicked">
			<div class="header-btn device-search-btn" @click="onSearchClicked">搜索</div>
			<div class="header-btn device-search-btn" @click="onResetSearchClicked">重置</div>
		</div>

		<div class="btn-container">
			<div class="device-page-title">{{ this.getListTitle() }}</div>

			<a-pagination v-model:current="this.page" simple :pageSize="this.pageSize"
			              :total="this.totalCount" @change="onPageChanged"
			              :showSizeChanger="false"/>

			<div class="header-btn device-search-btn" @click="onRefreshBtnClicked">刷新</div>
		</div>

		<div class="cs-main-container">
			<div class="list-container">
				<div class="device-empty" v-if="this.isLoaded === true && this.deviceList.length === 0">
					{{ this.isSearch === true ? '没有搜到匹配的设备' : '还没有设备开启过额外日志' }}
				</div>

				<template v-for="item in this.deviceList" :key="item.deviceId">
					<div class="item-container device-row">
						<div class="device-info">
							<div class="device-model">
								<span>{{ item.model || '未知型号' }}</span>
								<span class="device-version" v-if="this.getOsVersionText(item)">
									{{ this.getOsVersionText(item) }}
								</span>
							</div>

							<div class="device-id-line">
								<span class="device-id-text">{{ item.deviceId }}</span>
								<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
								     title="复制设备ID" @click="onCopyText(item.deviceId, '设备ID')">
							</div>

							<div class="device-sub-line device-email-line" v-if="item.email">
								<span>{{ item.email }}</span>
								<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
								     title="复制邮箱" @click="onCopyText(item.email, '邮箱')">
							</div>

							<div class="device-sub-line">
								<span :class="['online-dot', item.online ? 'is-online' : 'is-offline']"></span>
								<span>{{ item.online ? '在线' : '离线' }}</span>
								<span class="device-sub-divider">|</span>
								<span :style="this.getLogStyle(item)">{{ this.getLogText(item) }}</span>
							</div>

							<div class="device-sub-line" v-if="item.lastLogTimeString">
								最近日志：{{ item.lastLogTimeString }}
							</div>
							<div class="device-sub-line" v-if="item.updatedTimeString">
								开关更新：{{ item.updatedTimeString }}
							</div>
						</div>

						<div class="device-switch">
							<a-switch :checked="item.isOn === true"
							          :loading="this.updatingDeviceId === item.deviceId"
							          @change="onToggleExtraLog(item, $event)"/>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import {copyToClipboard, hideLoading, postRequestAsync, showLoading} from "@/utils/methods";

// 设备列表接口的超时（ms）
const DEVICE_LIST_TIMEOUT = 45000

export default {
	name: "DevicePage",
	data() {
		return {
			deviceList: [],
			searchKeyword: '',
			// 当前列表是搜索结果还是默认的开关列表，空列表的文案不一样
			isSearch: false,
			page: 1,
			pageSize: 20,
			totalCount: 0,
			// 区分"还没查"和"查完是空的"，否则一进页面就会闪一下空列表提示
			isLoaded: false,
			// 日志摘要是列表出来之后另外查的，这期间那一栏显示"查询中"而不是"未知"
			isLogLoading: false,
			logSummarySeq: 0,
			updatingDeviceId: null
		}
	},
	async mounted() {
		await this.getDeviceList()
	},
	methods: {
		async getDeviceList(isManual = false) {
			showLoading()
			const startedAt = Date.now()
			let result = null
			try {
				// 服务端要为这一页的每台设备现查 R2，R2 慢的时候默认的 15s 不够
				result = await postRequestAsync('admin/device/getExtraLogDevices', {
					keyword: this.searchKeyword.trim(),
					page: this.page
				}, false, DEVICE_LIST_TIMEOUT)
			} finally {
				hideLoading()
			}

			if (result?.code === 200) {
				this.deviceList = result?.msg?.list || []
				this.isSearch = result?.msg?.isSearch === true
				this.totalCount = result?.msg?.totalCount || 0
				this.pageSize = result?.msg?.pageSize || this.pageSize
				this.isLoaded = true
				if (isManual === true) {
					this.$message.success('刷新成功')
				}
				// 故意不 await：列表先出来，日志那一栏随后补上
				this.loadLogSummaries()
			} else if (!result && Date.now() - startedAt >= DEVICE_LIST_TIMEOUT) {
				// 请求层失败一律返回 null，拿不到原因，只能靠耗时判断是不是等到超时了。
				// 分开提示是因为两种情况的处理办法不一样：超时等一会儿再刷新，连不上要查网络 / 节点
				this.$message.error('获取设备列表超时，请稍后再刷新')
			} else {
				this.$message.error(`获取设备列表失败${result?.code ? `（${result.code}）` : '，服务器无响应'}`)
			}
		},
		/**
		 * 给当前这一页补上 R2 里的日志摘要。
		 *
		 * 查桶是服务端最慢的一步，所以从列表接口里拆了出来：列表只读库、马上就能渲染，
		 * 这边慢慢查。不弹全屏 loading，查的过程中那一栏显示"日志查询中…"。
		 */
		async loadLogSummaries() {
			// 翻页 / 搜索之后，上一页那次查询才回来的话要丢掉，不然会写到新列表上
			const seq = ++this.logSummarySeq
			const deviceIds = this.deviceList.map(item => item.deviceId).filter(Boolean)
			if (deviceIds.length === 0) {
				this.isLogLoading = false
				return
			}

			this.isLogLoading = true
			const result = await postRequestAsync('admin/device/getExtraLogSummaries', {deviceIds})
			if (seq !== this.logSummarySeq) {
				return
			}
			this.isLogLoading = false

			// 失败就保持原样（显示"日志状态未知"），不为这一栏单独弹错误
			const summaries = result?.code === 200 ? result?.msg?.summaries : null
			if (!summaries) {
				return
			}
			for (const item of this.deviceList) {
				const summary = summaries[item.deviceId]
				if (!summary) {
					continue
				}
				item.hasLog = summary.hasLog
				item.logCount = summary.logCount || 0
				item.logCountTruncated = summary.logCountTruncated === true
				// 查桶失败时 R2 这边没有时间，留着列表接口给的那个（库里记的上传时间）
				if (summary.lastLogTimeString) {
					item.lastLogTimeString = summary.lastLogTimeString
				}
			}
		},
		async onRefreshBtnClicked() {
			await this.getDeviceList(true)
		},
		/**
		 * 不带关键字时列的是开关表里的设备，带关键字时是从全量设备表搜出来的，
		 * 标题上说清楚现在看的是哪一种，不然搜完之后很容易以为这些设备都开着日志。
		 */
		getListTitle() {
			if (this.isSearch === true) {
				return `搜索结果（${this.totalCount}）`
			}
			return `额外日志设备（${this.totalCount}）`
		},
		/**
		 * 系统 + app 版本号。型号那一栏只放机型，版本单独跟在后面，
		 * 不然 android 机型解析出来之后就把版本号挤没了。
		 */
		getOsVersionText(item) {
			return [item?.os, item?.version].filter(Boolean).join(' ')
		},
		async onPageChanged(page) {
			this.page = page
			await this.getDeviceList()
		},
		async onSearchClicked() {
			// 换了关键字，之前那一页的页码就没意义了
			this.page = 1
			await this.getDeviceList()
		},
		async onResetSearchClicked() {
			if (!this.searchKeyword) {
				return
			}
			this.searchKeyword = ''
			this.page = 1
			await this.getDeviceList()
		},
		/**
		 * R2 里日志的展示文案。
		 * hasLog 为 null 表示查桶失败——这时候不能说"无日志"，那会让人以为设备没在传。
		 */
		getLogText(item) {
			if (item?.hasLog === null || item?.hasLog === undefined) {
				return this.isLogLoading === true ? '日志查询中…' : '日志状态未知'
			}
			if (item.hasLog !== true) {
				return '无日志'
			}
			return `有日志 ${item.logCount}${item.logCountTruncated === true ? '+' : ''} 条`
		},
		getLogStyle(item) {
			if (item?.hasLog === null || item?.hasLog === undefined) {
				return {'color': 'rgba(230, 213, 184, 0.6)'}
			}
			return {'color': item.hasLog === true ? '#52c41a' : '#ff6b6b'}
		},
		/**
		 * 复制任意一段文本。label 只用来拼提示语（"设备ID已复制"），
		 * 复制失败一律提示手动选中——浏览器在非安全上下文里会直接拒绝写剪贴板。
		 */
		async onCopyText(text, label) {
			if (!text) {
				return
			}
			const ok = await copyToClipboard(text)
			if (ok) {
				this.$message.success(`${label}已复制`)
			} else {
				this.$message.error('复制失败，请手动选中复制')
			}
		},
		/**
		 * 切换额外日志上报开关，复用工单详情页那套接口。
		 *
		 * 用 :checked + @change 而不是 v-model：接口失败时开关不会停在错误的位置上，
		 * 只有服务端确认成功才更新本地状态。
		 */
		async onToggleExtraLog(item, checked) {
			if (!item?.deviceId || this.updatingDeviceId) {
				return
			}
			this.updatingDeviceId = item.deviceId
			try {
				const result = await postRequestAsync('admin/cs/updateExtraLogSwitch', {
					deviceId: item.deviceId,
					isOn: checked
				})
				if (result?.code === 200) {
					item.isOn = checked
					// delivered 只表示是否已即时推给在线设备；为 false 时开关已存库，
					// 设备下次连上会自动拿到，不算失败
					if (checked) {
						this.$message.success(
							result?.msg?.delivered === true ? '已开启，指令已下发到设备' : '已开启，设备当前不在线，上线后生效'
						)
					} else {
						this.$message.success('已关闭额外日志上报')
					}
				} else {
					this.$message.error(`操作失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.updatingDeviceId = null
			}
		}
	}
}
</script>

<style scoped>
.device-page-container {
	min-height: 100vh;
	position: relative;
	background-color: #191123;
	overflow-x: hidden;
	width: 100vw;
	padding: 1em 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.device-search-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	gap: 0.6em;
}

.device-search-input {
	flex: 1;
	/* flex 子项默认 min-width:auto，输入框会被内容撑开把按钮挤出屏幕 */
	min-width: 0;
	max-width: 30em;
	color: #E6D5B8;
	background-color: transparent;
	border: 1px solid rgba(230, 213, 184, 0.6);
	border-radius: 0.2em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.2em 0.8em !important;
	font-size: 1.2em;
	line-height: 1.2em;
	outline: none;
}

.device-search-input:focus {
	border-color: #FF8900;
}

.device-search-input::placeholder {
	color: rgba(230, 213, 184, 0.4);
}

/* 标题 | 分页 | 刷新 三段，窄屏放不下时整行换行。
   左右内边距和搜索行、列表保持一致，刷新按钮的右边缘才能跟"重置"、卡片对齐 */
.btn-container {
	flex-wrap: wrap;
	row-gap: 0.4em;
	padding: 0 1em;
	margin-bottom: 1em;
}

/* 分页 / 搜索框 / 按钮统一对齐工单页的类目切换按钮：
   同为 font-size 1.2em，高度 = 行高 1.2em + 上下各 0.2em 内边距 + 1px 边框 */
.btn-container :deep(.ant-pagination) {
	font-size: 1.2em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-prev),
.btn-container :deep(.ant-pagination-simple .ant-pagination-next),
.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager) {
	height: calc(1.6em + 2px);
	line-height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	margin-inline-end: 0.4em;
}

/* 翻页组最后一项的尾随间距要去掉，否则这一行的右边比其它行少 0.4em，看着不齐 */
.btn-container :deep(.ant-pagination-simple .ant-pagination-next) {
	margin-inline-end: 0;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-item-link) {
	display: flex;
	align-items: center;
	justify-content: center;
	height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	border-radius: 0.2em;
	font-size: 1em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager input) {
	height: calc(1.6em + 2px);
	/* 页码框不画边框，只留数字；宽度收窄到刚好放下页码。
	   App.vue 里有全局的 .ant-pagination.ant-pagination-simple input {border: 1px ... !important}，
	   不加 !important 盖不掉 */
	border: none !important;
	box-shadow: none;
	background-color: transparent;
	min-width: 2em;
	border-radius: 0.2em;
	font-size: 1em;
	line-height: 1.2em;
	text-align: center;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0 0.2em !important;
	margin-right: 0.4em;
}

/* 全局 .header-btn 比搜索框矮一截，这里统一成和输入框一样的高度 */
.device-search-btn {
	font-size: 1.2em;
	line-height: 1.2em;
	padding: 0.2em 1em;
	border-radius: 0.2em;
	white-space: nowrap;
}

.device-page-title {
	color: #E6D5B8;
	font-size: 1.2em;
	padding: 0.8em 0;
}

@media only screen and (max-width: 900px) {
	.device-search-container {
		padding: 0 1.2em;
		gap: 0.5em;
	}

	.btn-container {
		padding: 0 1.2em;
	}

	.device-search-btn {
		padding: 0.2em 0.7em;
	}
}

.device-empty {
	color: rgba(230, 213, 184, 0.6);
	padding: 2em 0;
	text-align: center;
	width: 100%;
}

/* 左边设备信息、右边开关。item-container 是全局的纵向排列，这里改成横向 */
.device-row {
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: 0.8em;
	cursor: default;
	gap: 0.8em;
}

.device-info {
	display: flex;
	flex-direction: column;
	/* #app 上有全局的 text-align: center，不显式改回来型号和状态那几行会居中 */
	text-align: left;
	align-items: flex-start;
	/* 不加 min-width:0 的话，长设备ID会把这一栏撑开、把开关挤出卡片 */
	min-width: 0;
	flex: 1;
}

.device-model {
	color: #E6D5B8;
	font-size: 1.1em;
	margin-bottom: 0.2em;
	word-break: break-all;
}

.device-version {
	color: rgba(230, 213, 184, 0.6);
	font-size: 0.85em;
	margin-left: 0.5em;
	white-space: nowrap;
}

.device-id-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.4em;
	min-width: 0;
}

.device-id-text {
	color: rgba(230, 213, 184, 0.85);
	font-size: 1.15em;
	word-break: break-all;
}

/* 邮箱要和设备id 一样醒目，不跟着 .device-sub-line 的 0.9em 缩小。
   两个类写在一起是为了压过后面 .device-sub-line 的 font-size —— 同特异性时后定义的赢 */
.device-sub-line.device-email-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.4em;
	min-width: 0;
	font-size: 1.15em;
	color: rgba(230, 213, 184, 0.85);
}

.copy-text-btn {
	width: 1em;
	height: 1em;
	flex: 0 0 auto;
	cursor: pointer;
	opacity: 0.7;
}

.copy-text-btn:hover {
	opacity: 1;
}

.device-sub-line {
	color: rgba(230, 213, 184, 0.6);
	font-size: 0.9em;
	margin-top: 0.2em;
	word-break: break-all;
}

.device-sub-divider {
	padding: 0 0.5em;
	color: rgba(230, 213, 184, 0.3);
}

.device-switch {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
}

.online-dot {
	display: inline-block;
	width: 0.5em;
	height: 0.5em;
	border-radius: 50%;
	margin-right: 0.4em;
	vertical-align: middle;
}

.online-dot.is-online {
	background-color: #52c41a;
}

.online-dot.is-offline {
	background-color: rgba(230, 213, 184, 0.4);
}
</style>
